import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Toolbar,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { CmpTypographyField } from '../../../component/ToolBox/ToolBox';
import { exportToExcel, fAlertToast } from '../../../Utility/Utilitys';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { DataGridPro, GridEditInputCell } from '@mui/x-data-grid-pro';

const toolbar = {
  backgroundColor: '#0d73bb',
  color: 'white',
  display: 'flex',
  justifyContent: 'space-between',
  minHeight: '40px !important'
};

export const BufferQuantityVarianModal = ({
  data,
  open,
  onClose,
  subDataGrid,
  setSubDataGrid,
  calculate_Qty_Variant,
  title
}) => {
  const vMediaQuery = useMediaQuery('max-width:900px');

  const to2 = (n) => Number(Number(n).toFixed(2));

  // keep '' while editing; use 0 for math
  const updateValues = (newRow, oldRow = {}) => {
    const weight = Number(newRow.GMS_Weight ?? oldRow.GMS_Weight) || 0;

    const piecesRaw = newRow.GM_Pieces ?? oldRow.GM_Pieces ?? '';
    const piecesNum = piecesRaw === '' ? 0 : Math.max(0, Number(piecesRaw) || 0);

    const revQty = Number(newRow.ReceivedQty ?? oldRow.ReceivedQty) || 0;

    const costRaw = newRow.GMS_Cost ?? oldRow.GMS_Cost ?? '';
    const costNum = costRaw === '' ? 0 : Math.max(0, Number(costRaw) || 0);

    return {
      ...oldRow,
      ...newRow,
      GM_Pieces: piecesRaw, // keep '' while typing
      GMS_Cost: costRaw, // keep '' while typing
      GMS_NetWeight: to2(revQty * piecesNum * weight),
      GMS_NetCost: to2(costNum * revQty)
    };
  };

  const setGM_Pieces = (params) => {
    const { row, value } = params;
    const nextRow = updateValues({ ...row, GM_Pieces: value }, row);
    const updatedRows = subDataGrid.map((itm) => (itm.id === nextRow.id ? nextRow : itm));
    setSubDataGrid(updatedRows);
    return nextRow;
  };
  const setGMS_Cost = (params) => {
    const { row, value } = params;
    const nextRow = updateValues({ ...row, GMS_Cost: value }, row);
    const updatedRows = subDataGrid.map((itm) => (itm.id === nextRow.id ? nextRow : itm));
    setSubDataGrid(updatedRows);
    return nextRow;
  };

  //focus the edit field one by one
  const fieldToIdPrefix = (field) => (field === 'GM_Pieces' ? 'pieces' : 'cost');
  const idFor = (field, rowIdx) => `${fieldToIdPrefix(field)}-${rowIdx}`;

  const focusCellByIndex = (api, rowIndex, field) => {
    if (rowIndex < 0) return;
    // Bring target row into view first
    api.scrollToIndexes({ rowIndex });

    // Focus the specific input by ID after the row renders
    const targetId = idFor(field, rowIndex);
    // allow the grid to render the row, sometimes two frames helps with virtualization
    requestAnimationFrame(() => {
      const el1 = document.getElementById(targetId);
      if (el1) {
        el1.focus();
        return;
      }
      requestAnimationFrame(() => {
        document.getElementById(targetId)?.focus();
      });
    });
  };

  // focus to edit field each row in horizontally  when 0 it changed '' Optional: reverse on Shift+Enter
  const isShiftEnter = (e) => e.key === 'Enter' && e.shiftKey;
  const isEnterOnly = (e) => e.key === 'Enter' && !e.shiftKey;

  const isZeroish = (v) => {
    const s = String(v ?? '').trim();
    if (s === '') return false;
    const n = Number(s);
    return !Number.isNaN(n) && n === 0;
  };

  const fContent_Focus = (params, e, setSubDataGrid) => {
    // params.id -> row id, params.field -> column field
    const { id, field } = params;
    const raw = e.target.value;

    if (!isZeroish(raw)) return;

    setSubDataGrid((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: '' } : r)));
  };

  // blur to edit field each row in horizontally  when ' ' it changed 0
  const toFixedNum = (n, d = 2) => Number(Number(n).toFixed(d));
  const toNumOr0 = (s) => {
    const t = String(s ?? '').trim();
    if (t === '') return 0;
    const n = Number(t);
    return Number.isFinite(n) ? n : 0;
  };

  // params-based blur (recommended)
  const fContent_Blur = (params, e, setSubDataGrid, { decimals = 2, min = 0 } = {}) => {
    const { id, field, row } = params;
    const raw = e.target.value;
    const num = toNumOr0(raw);
    const clamped = Math.max(min, num);
    const committed = decimals == null ? clamped : toFixedNum(clamped, decimals);

    setSubDataGrid((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const next = { ...r, [field]: committed };
        // If you have updateValues(newRow, oldRow), keep derived fields in sync:
        return typeof updateValues === 'function' ? updateValues(next, r) : next;
      })
    );
  };

  const validateRow = (row) => {
    const pieces = Number(row.GM_Pieces ?? row.GM_Pieces ?? 0);
    const cost = Number(row.GMS_Cost ?? 0);

    const errors = [];

    // Rule 1: CTN must be > 0 when Pieces != 0
    if (pieces !== 0 && cost <= 0) {
      errors.push('Cost must be > 0 when Pieces ≠ 0');
    }

    // Rule 2: Pieces must be > 0 when CTN != 0
    if (cost !== 0 && pieces <= 0) {
      errors.push('Pieces must be > 0 when Cost ≠ 0');
    }

    return { valid: errors.length === 0, errors, pieces, cost };
  };

  const validateRowsById = (rows) => {
    const errorsById = {};
    for (const r of rows) {
      const { valid, errors } = validateRow(r);
      if (!valid) errorsById[r.id] = errors;
    }
    return {
      valid: Object.keys(errorsById).length === 0,
      errorsById
    };
  };

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 140,
      hide: true
    },
    {
      field: 'GMS_AltItmCd',
      headerName: 'ItemCode',
      width: 140,
      hide: true
    },
    {
      field: 'GMS_Name1',
      headerName: 'ItemName',
      width: 170
    },
    {
      field: 'GMS_CTN',
      headerName: 'Purchased',
      width: 90,
      type: 'number',
      editable: false
    },
    {
      field: 'ReceivedQty',
      headerName: 'Received',
      width: 90,
      type: 'number',
      editable: false
    },
    {
      field: 'GMS_PRQty',
      headerName: 'Return',
      width: 90,
      type: 'number',
      editable: false
    },
    {
      field: 'GM_Pieces',
      headerName: 'Pieces',
      width: 90,
      type: 'number',
      cellClassName: 'editable-cell',
      renderCell: (params) => {
        const { row } = params;
        if (row.isSummaryRow) return <span>{params.value}</span>;
        const rowIdx = params.api.getRowIndexRelativeToVisibleRows(params.id);
        const myId = idFor('GM_Pieces', rowIdx);
        return (
          <TextField
            id={myId}
            name={params.field}
            value={row?.GM_Pieces ?? ''} // show whatever is in state ('' allowed)
            onKeyDown={(e) => {
              // stop grid arrow nav
              if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key))
                e.stopPropagation();
              // block minus / exponent
              if (e.key === '-' || e.key.toLowerCase() === 'e') e.preventDefault();

              // Enter -> focus same-row Pieces
              if (isEnterOnly(e)) {
                e.preventDefault();
                e.stopPropagation();
                focusCellByIndex(params.api, rowIdx, 'GMS_Cost');
              }
              // Shift+Enter -> go to previous row's Pieces (optional)
              if (isShiftEnter(e)) {
                e.preventDefault();
                e.stopPropagation();
                focusCellByIndex(params.api, rowIdx - 1, 'GMS_Cost');
              }
            }}
            onChange={(e) => {
              const v = e.target.value;
              // allow '' while typing; otherwise keep raw text and sanitize later
              if (v === '') setGM_Pieces({ ...params, value: '' });
              else setGM_Pieces({ ...params, value: v });
            }}
            onFocus={(e) => fContent_Focus(params, e, setSubDataGrid)}
            onBlur={(e) => fContent_Blur(params, e, setSubDataGrid, { decimals: 2, min: 0 })}
            variant="outlined"
            size="small"
            inputProps={{
              inputMode: 'decimal',
              type: 'text', // <-- important: allow empty/backspace
              pattern: '[0-9]*[.]?[0-9]*',
              style: { textAlign: 'center' }
            }}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-input': {
                textAlign: 'right',
                padding: '4px 12px',
                fontSize: '13px',
                backgroundColor: '#edfaff',
                // border: '1px solid lightgray',
                borderRadius: '6px !important'
              }
            }}
          />
        );
      }
    },
    {
      field: 'GMS_Weight',
      headerName: 'Weight',
      width: 100,
      type: 'number'
    },
    {
      field: 'GMS_NetWeight',
      headerName: 'Net Weight',
      width: 120,
      type: 'number',
      editable: false,
      cellClassName: 'readonly-cell'
    },
    {
      field: 'GMS_Cost',
      headerName: 'Cost',
      width: 100,
      type: 'number',
      //editable: true,
      cellClassName: 'editable-cell',
      renderCell: (params) => {
        const { row } = params;
        if (row.isSummaryRow) return <span>{params.value}</span>;
        const rowIdx = params.api.getRowIndexRelativeToVisibleRows(params.id);
        const myId = idFor('GMS_Cost', rowIdx);
        return (
          <TextField
            id={myId}
            name={params.field}
            value={row?.GMS_Cost ?? ''} // show whatever is in state ('' allowed)
            onKeyDown={(e) => {
              if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key))
                e.stopPropagation();
              if (e.key === '-' || e.key.toLowerCase() === 'e') e.preventDefault();

              // Enter -> focus next row's CTN
              if (isEnterOnly(e)) {
                e.preventDefault();
                e.stopPropagation();
                focusCellByIndex(params.api, rowIdx + 1, 'GM_Pieces');
              }
              // Shift+Enter -> focus same-row CTN (optional)
              if (isShiftEnter(e)) {
                e.preventDefault();
                e.stopPropagation();
                focusCellByIndex(params.api, rowIdx, 'GM_Pieces');
              }
            }}
            onChange={(e) => {
              const v = e.target.value;
              // allow '' while typing; otherwise keep raw text and sanitize later
              if (v === '') setGMS_Cost({ ...params, value: '' });
              else setGMS_Cost({ ...params, value: v });
            }}
            onFocus={(e) => fContent_Focus(params, e, setSubDataGrid)}
            onBlur={(e) => fContent_Blur(params, e, setSubDataGrid, { decimals: 2, min: 0 })}
            variant="outlined"
            size="small"
            inputProps={{
              inputMode: 'decimal',
              type: 'text', // <-- important: allow empty/backspace
              pattern: '[0-9]*[.]?[0-9]*',
              style: { textAlign: 'center' }
            }}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-input': {
                textAlign: 'right',
                padding: '4px 12px',
                fontSize: '13px',
                backgroundColor: '#edfaff',
                // border: '1px solid lightgray',
                borderRadius: '6px !important'
              }
            }}
          />
        );
      }
    },
    {
      field: 'GMS_NetCost',
      headerName: 'Net Amount',
      width: 120,
      type: 'number',
      cellClassName: 'readonly-cell'
    }
  ];

  const fApply = () => {
    // validate every row (or filter to updated ones if you prefer)
    const { valid, errorsById } = validateRowsById(subDataGrid);

    if (!valid) {
      const [firstId, msgs] = Object.entries(errorsById)[0];
      fAlertToast('FAILED', `Row ID ${firstId}: ${msgs[0]}`);
      return;
    }
    let packQty = 0;
    if (totals?.ReceivedQty && totals.ReceivedQty !== 0) {
      packQty = totals.GMS_NetWeight / totals.ReceivedQty;
    }

    let unitRate = 0;
    if (totals?.ReceivedQty && totals.ReceivedQty !== 0) {
      unitRate = totals.GMS_NetCost / totals.ReceivedQty;
    }
    console.log(
      'data:',
      data,
      totals.ReceivedQty.toFixed(3),
      unitRate.toFixed(4),
      packQty.toFixed(3)
    );

    calculate_Qty_Variant(
      data,
      totals.ReceivedQty.toFixed(3),
      unitRate.toFixed(4),
      packQty.toFixed(3)
    );

    onClose();
  };

  const handleExport = () => {
    exportToExcel(subDataGrid, 'BufferQuantity_data', 'BufferQuantity');
  };

  console.log('sub', subDataGrid);

  const totals = React.useMemo(() => {
    return subDataGrid.reduce(
      (a, r) => ({
        GMS_CTN: a.GMS_CTN + Number(r.GMS_CTN || 0),
        GMS_NetWeight: a.GMS_NetWeight + Number(r.GMS_NetWeight || 0),
        GMS_NetCost: a.GMS_NetCost + Number(r.GMS_NetCost || 0),
        GMS_PRQty: a.GMS_PRQty + Number(r.GMS_PRQty || 0),
        ReceivedQty: a.ReceivedQty + Number(r.ReceivedQty || 0)
      }),
      {
        GMS_CTN: 0,
        GMS_NetWeight: 0,
        GMS_NetCost: 0,
        GMS_PRQty: 0,
        ReceivedQty: 0
      }
    );
  }, [subDataGrid]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg">
      <Toolbar sx={toolbar}>
        <DialogTitle
          sx={{
            backgroundColor: '#0d73bb',
            color: 'white',
            padding: '0 4px !important'
          }}>
          {title}
        </DialogTitle>
        <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </Toolbar>

      <DialogContent sx={{ '&.MuiDialogContent-root': { padding: '10px !important' } }}>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={5} md={5.5} lg={5.5}>
            <Grid
              container
              spacing={1}
              columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}
              sx={{ alignItems: 'center' }}>
              <Grid item xs={7} sm={2} lg={1.5} md={1.5}>
                <CmpTypographyField xcomponent={'span'} xText={'Item'} />
              </Grid>
              <Grid item xs={1} sm={0.5} lg={0.5} md={0.5}>
                <CmpTypographyField xcomponent={'span'} xText={':'} />
              </Grid>
              <Grid item xs={2} sm={9.5} lg={10} md={10}>
                <CmpTypographyField
                  xcomponent={'span'}
                  xText={`${data?.ALTItmCd} -${data?.ALTItmName}`}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={2} md={2} lg={2}>
            <Grid
              container
              spacing={1}
              columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}
              sx={{ alignItems: 'center' }}>
              <Grid item xs={7} sm={7} lg={6} md={4}>
                <CmpTypographyField xcomponent={'span'} xText={'Pur.CTN'} />
              </Grid>
              <Grid item xs={1} sm={1} lg={1} md={1}>
                <CmpTypographyField xcomponent={'span'} xText={':'} />
              </Grid>
              <Grid item xs={2} sm={4} lg={4} md={7}>
                <CmpTypographyField xcomponent={'span'} xText={data.Qty} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={2.5} md={2} lg={2}>
            <Grid
              container
              spacing={1}
              columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}
              sx={{ alignItems: 'center' }}>
              <Grid item xs={7} sm={6} lg={6} md={6}>
                <CmpTypographyField xcomponent={'span'} xText={'Received'} />
              </Grid>
              <Grid item xs={1} sm={1} lg={1} md={1}>
                <CmpTypographyField xcomponent={'span'} xText={':'} />
              </Grid>
              <Grid item xs={2} sm={5} lg={5} md={5}>
                <CmpTypographyField xcomponent={'span'} xText={data.ReceivedQty} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={2.5} md={2.5} lg={2.5}>
            <Grid
              container
              spacing={1}
              columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}
              sx={{ alignItems: 'center' }}>
              <Grid item xs={7} sm={3} lg={3} md={3}>
                <CmpTypographyField xcomponent={'span'} xText={'PR.CTN'} />
              </Grid>
              <Grid item xs={1} sm={1} lg={1} md={1}>
                <CmpTypographyField xcomponent={'span'} xText={':'} />
              </Grid>
              <Grid item xs={2} sm={6} lg={6} md={6}>
                <CmpTypographyField xcomponent={'span'} xText={data.PRQty} />
              </Grid>
              <Grid item xs={2} sm={2} lg={2} md={2}>
                <Tooltip title="Export">
                  <IconButton
                    color="primary"
                    onClick={handleExport}
                    size="small"
                    sx={{
                      padding: 0,
                      '& .MuiSvgIcon-root': {
                        fontSize: '20px'
                      }
                    }}>
                    <DownloadOutlinedIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                textAlign: 'right',
                alignItems: 'flex-end'
              }}></Box>
            <Box
              sx={{
                height: '55vh',
                width: '100%'
              }}>
              <DataGridPro
                className="gridTable"
                rowHeight={30}
                headerHeight={45}
                rows={subDataGrid}
                columns={columns}
                getRowId={(row) => row.id}
                pinnedRows={{
                  bottom: [
                    {
                      id: 'footer',
                      GMS_Name1: 'TOTAL',
                      GMS_CTN: totals.GMS_CTN.toFixed(2),
                      ReceivedQty: totals.ReceivedQty.toFixed(2),
                      GMS_NetCost: totals.GMS_NetCost.toFixed(3),
                      GMS_PRQty: totals.GMS_PRQty.toFixed(2),
                      GMS_NetWeight: totals.GMS_NetWeight.toFixed(4),
                      isSummaryRow: true
                    }
                  ]
                }}
                initialState={{
                  columns: {
                    columnVisibilityModel: {
                      id: false,
                      GMS_AltItmCd: false
                    }
                  }
                }}
                disableRowSelectionOnClick
                isCellEditable={(params) => !params.row?.isSummaryRow}
                getRowClassName={(params) => (params.row?.isSummaryRow ? 'summary-row' : '')}
                isRowSelectable={(params) => !params.row?.isSummaryRow}
                hideFooter
              />
            </Box>

            <Box textAlign={'center'}>
              <Button
                onClick={fApply}
                variant="contained"
                size="small"
                sx={{ m: 1 }}
                disabled={title === 'Quantity Variant - Confirmation'}>
                Apply
              </Button>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};
